// YouTube Cookies 管理页 — v2.6.19.
//
// 单文件而非账号池 (vs /x-accounts):一次上传一个 Netscape 格式 cookies.txt,
// 后端原子替换 → 立即跑 yt-dlp 探测 → 把 alive/dead + reason 同步回前端。
// 这页只显示元数据,从不接触 cookies 内容。
//
// 替换流程:浏览器登录 YouTube → "Get cookies.txt LOCALLY" 扩展导出 →
// 这里拖到上传框 → 后端 ~5-8 秒响应(yt-dlp 在 canary 视频上探测)→
// 操作员看到绿色 alive 就走人。如果红色 dead,响应里带 yt-dlp 错误尾,
// 一般原因是账号被风控或导出时漏了 __Secure-3PSID。

import { useState, type ChangeEvent, type DragEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  APIError,
  type YouTubeCookiesStatus,
  type YouTubeCookiesUploadResponse,
} from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { SectionTabs, SYSTEM_TABS } from "@/components/SectionTabs";

// FAILURE_TYPE_LABEL / _HINT — v2.17.2. Mirrors the branches in
// telegram.FormatYouTubeCookiesAlert so the page and the alert can't
// give contradictory advice. Only cookies_expired should ever tell the
// operator to re-export; everything else here has a different fix, and
// re-exporting during those wastes a valid browser session.
const FAILURE_TYPE_LABEL: Record<string, string> = {
  cookies_expired: "Cookie 已失效",
  bot_challenge: "YouTube 临时风控",
  vpn_down: "VPN tun 故障",
  vpn_routing_lost: "VPN 路由规则丢失",
  probe_transient: "探针瞬时失败",
  probe_timeout: "探针超时",
  unknown: "未分类",
};

const FAILURE_TYPE_HINT: Record<string, string> = {
  cookies_expired: "需要重新导出 Cookie(下面的上传/粘贴)。",
  bot_challenge: "Cookie 大概率仍然有效,先别动 —— 观察 1-2 小时通常自愈。",
  vpn_down: "去修 WireGuard(`systemctl restart wg-quick@surfshark`),不要重新导出 Cookie。",
  vpn_routing_lost: "ssh prod 跑 `systemctl restart wg-policy-routing`,不要重新导出 Cookie。",
  probe_transient: "探针自己的问题,不代表 Cookie 失效 —— 已自动重试过,下一轮 cron 大概率恢复。",
  probe_timeout: "探针被超时杀掉,没给出结论,不代表 Cookie 失效。",
};

// uploadResultMessage — v2.17.2. Three outcomes, not two.
//
// The old code had only pass/fail and rendered every non-pass as
// "yt-dlp 探测失败", which is what made a transient blip look identical
// to an expired session. The probe now reports a third state: it ran,
// it couldn't reach a verdict, and it deliberately did NOT touch the
// stored health. Saying "失效" there sends the operator off to re-export
// a perfectly good cookies file — the exact wasted trip this whole fix
// exists to prevent.
function uploadResultMessage(
  r: YouTubeCookiesUploadResponse,
  verb: string,
): { kind: "ok" | "err"; text: string } {
  if (r.probe_alive) {
    return { kind: "ok", text: `✓ ${verb}成功 (${r.file_size} bytes),yt-dlp 探测通过` };
  }
  if (r.probe_inconclusive) {
    return {
      kind: "ok",
      text:
        r.message ??
        `✓ ${verb}成功 (${r.file_size} bytes)。探针暂时无法确认(不代表 Cookie 失效),后台 5 分钟一次的 cron 会自动重试。`,
    };
  }
  return {
    kind: "err",
    text: `⚠️ Cookies 已写入,但 yt-dlp 探测失败: ${r.probe_error ?? "未知错误"}`,
  };
}

export default function YouTubeCookies() {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["youtube-cookies"],
    queryFn: () => endpoints.youtubeCookiesStatus(),
    refetchInterval: 30_000,
  });

  const [uploadMsg, setUploadMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [dragging, setDragging] = useState(false);

  const replaceM = useMutation({
    mutationFn: (file: File) => endpoints.youtubeCookiesReplace(file),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["youtube-cookies"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "badges"] });
      setUploadMsg(uploadResultMessage(r, "上传"));
    },
    onError: (e: unknown) => {
      const msg = e instanceof APIError ? e.message : e instanceof Error ? e.message : String(e);
      setUploadMsg({ kind: "err", text: `✗ 上传失败: ${msg}` });
    },
  });

  // v2.6.20 — paste-text mode. Calls a separate JSON endpoint and
  // shares the same onSuccess/onError handling.
  const [pasteText, setPasteText] = useState("");
  const pasteM = useMutation({
    mutationFn: (text: string) => endpoints.youtubeCookiesReplaceText(text),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["youtube-cookies"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "badges"] });
      setPasteText("");
      setUploadMsg(uploadResultMessage(r, "粘贴"));
    },
    onError: (e: unknown) => {
      const msg = e instanceof APIError ? e.message : e instanceof Error ? e.message : String(e);
      setUploadMsg({ kind: "err", text: `✗ 粘贴失败: ${msg}` });
    },
  });

  const clearM = useMutation({
    mutationFn: () => endpoints.youtubeCookiesClear(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["youtube-cookies"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "badges"] });
      setUploadMsg({ kind: "ok", text: "✓ 已清空,归档 cron 将自动 skip" });
    },
    onError: (e: unknown) => {
      const msg = e instanceof APIError ? e.message : e instanceof Error ? e.message : String(e);
      setUploadMsg({ kind: "err", text: `✗ 清空失败: ${msg}` });
    },
  });

  const onFile = (file: File | null) => {
    if (!file) return;
    setUploadMsg(null);
    replaceM.mutate(file);
  };
  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => onFile(e.target.files?.[0] ?? null);
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    onFile(e.dataTransfer.files?.[0] ?? null);
  };

  const status = statusQ.data;

  return (
    <div className="flex h-full flex-col">
      <SectionTabs tabs={SYSTEM_TABS} />
      <header className="border-b border-border bg-background px-6 py-4">
        <h1 className="text-lg font-semibold">YouTube Cookies 管理</h1>
        <p className="text-sm text-muted-foreground">
          单文件 (`/opt/bitbase-kol/secrets/youtube_cookies.txt`) — 替换后立即 yt-dlp 探测
        </p>
      </header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {!statusQ.data && statusQ.isLoading && (
          <div className="text-sm text-muted-foreground">加载中…</div>
        )}

        {status && !status.configured && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            后端未配置 cookies 路径 (`archive.ytdlp_cookies_path` 为空)。当前部署不需要 YouTube 归档,该页面只读。
          </div>
        )}

        {status && status.configured && (
          <>
            <StatusCard status={status} />
            <UploadCard
              dragging={dragging}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onInputChange={onInputChange}
              uploading={replaceM.isPending}
            />

            <PasteCard
              text={pasteText}
              onTextChange={setPasteText}
              onSubmit={() => {
                setUploadMsg(null);
                pasteM.mutate(pasteText);
              }}
              pasting={pasteM.isPending}
            />

            {uploadMsg && (
              <div
                className={`rounded-md border px-4 py-3 text-sm ${
                  uploadMsg.kind === "ok"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : "border-rose-300 bg-rose-50 text-rose-700"
                }`}
              >
                {uploadMsg.text}
              </div>
            )}

            <ClearCard
              filePresent={status.file_present}
              onClick={() => {
                if (confirm("清空 cookies 文件?归档 cron 会自动停止下载,直到下次重新上传。")) {
                  clearM.mutate();
                }
              }}
              clearing={clearM.isPending}
            />

            <HelpCard />
          </>
        )}
      </div>
    </div>
  );
}

function StatusCard({ status }: { status: YouTubeCookiesStatus }) {
  const alive = status.probe_alive;
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-3">
        <span
          className={`inline-flex h-2.5 w-2.5 rounded-full ${
            alive ? "bg-emerald-500" : status.file_present ? "bg-rose-500" : "bg-zinc-400"
          }`}
        />
        <h2 className="text-base font-semibold">
          {alive ? "Alive" : status.file_present ? "Dead" : "未配置 / 空"}
        </h2>
        {!alive && status.probe_failure_type && (
          <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
            {FAILURE_TYPE_LABEL[status.probe_failure_type] ?? status.probe_failure_type}
            {status.probe_consecutive_dead ? ` · 连续 ${status.probe_consecutive_dead} 次` : ""}
          </span>
        )}
      </div>
      {!alive && status.probe_failure_type && FAILURE_TYPE_HINT[status.probe_failure_type] && (
        <p className="mt-2 text-xs text-muted-foreground">
          {FAILURE_TYPE_HINT[status.probe_failure_type]}
        </p>
      )}
      <dl className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
        <div>
          <dt className="text-muted-foreground">文件存在</dt>
          <dd>{status.file_present ? "是" : "否"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">文件大小</dt>
          <dd>{status.file_size != null ? `${status.file_size} bytes` : "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Cookie 行数</dt>
          <dd>{status.cookie_lines ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">最后替换</dt>
          <dd>{status.modified_at ? new Date(status.modified_at).toLocaleString() : "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">最后探测</dt>
          <dd>
            {status.probe_checked_at
              ? new Date(status.probe_checked_at).toLocaleString()
              : "尚未探测"}
          </dd>
        </div>
        {!alive && status.probe_reason && (
          <div className="md:col-span-2">
            <dt className="text-muted-foreground">探测失败原因</dt>
            <dd className="break-all rounded bg-rose-50 px-2 py-1 font-mono text-xs text-rose-700">
              {status.probe_reason}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function UploadCard({
  dragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onInputChange,
  uploading,
}: {
  dragging: boolean;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  uploading: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <h2 className="mb-2 text-base font-semibold">替换 cookies</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        浏览器登录 YouTube → 用 "Get cookies.txt LOCALLY" 扩展导出 cookies.txt → 拖到下面 或 点击选择。
        替换后会立即跑 yt-dlp 探测,5-8 秒返回 alive/dead。
      </p>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-8 text-sm transition ${
          dragging ? "border-sky-400 bg-sky-50" : "border-border bg-muted/30"
        } ${uploading ? "opacity-60" : ""}`}
      >
        {uploading ? (
          <span className="text-muted-foreground">上传中,等 yt-dlp 探测…</span>
        ) : (
          <>
            <span className="text-muted-foreground">拖动 cookies.txt 到这里</span>
            <span className="my-2 text-xs text-muted-foreground">或</span>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".txt,text/plain"
                className="hidden"
                onChange={onInputChange}
              />
              <span className="inline-flex items-center rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted">
                选择文件
              </span>
            </label>
          </>
        )}
      </div>
    </div>
  );
}

function PasteCard({
  text,
  onTextChange,
  onSubmit,
  pasting,
}: {
  text: string;
  onTextChange: (s: string) => void;
  onSubmit: () => void;
  pasting: boolean;
}) {
  const canSubmit = text.trim().length > 50 && !pasting;
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <h2 className="mb-2 text-base font-semibold">直接粘贴 cookies</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        如果不想拖文件,可以直接把整个 cookies.txt 内容(从 `# Netscape HTTP Cookie File`
        开始)粘到下面,点提交。同样会立即跑 yt-dlp 探测。
      </p>
      <textarea
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder={"# Netscape HTTP Cookie File\n# https://curl.haxx.se/rfc/cookie_spec.html\n.youtube.com\\tTRUE\\t/\\tFALSE\\t...\\tSID\\t...\n..."}
        rows={8}
        spellCheck={false}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
      />
      <div className="mt-3 flex items-center gap-3">
        <Button size="sm" disabled={!canSubmit} onClick={onSubmit}>
          {pasting ? "提交中,等 yt-dlp 探测…" : "提交粘贴的 cookies"}
        </Button>
        <span className="text-xs text-muted-foreground">
          {text.length > 0 ? `${text.length} 字符` : "建议直接 Ctrl+A 复制扩展导出的全部内容"}
        </span>
      </div>
    </div>
  );
}

function ClearCard({
  filePresent,
  onClick,
  clearing,
}: {
  filePresent: boolean;
  onClick: () => void;
  clearing: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <h2 className="mb-2 text-base font-semibold">清空 cookies</h2>
      <p className="mb-3 text-sm text-muted-foreground">
        把文件 truncate 到 0 字节。归档 cron (`archive_bb_deliverables`) 会立刻 skip,直到下次重新上传。
        通常只在账号被冻结、要换号时用。
      </p>
      <Button variant="outline" size="sm" disabled={!filePresent || clearing} onClick={onClick}>
        {clearing ? "清空中…" : "清空 cookies 文件"}
      </Button>
    </div>
  );
}

function HelpCard() {
  return (
    <div className="rounded-md border border-border bg-card p-4 text-sm">
      <h2 className="mb-2 text-base font-semibold">操作要点</h2>
      <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
        <li>
          <strong>必备 cookies</strong>:`SID` / `__Secure-3PSID` / `SAPISID` 三个必须在,缺一个上传会被拒。
        </li>
        <li>
          <strong>账号建议</strong>:用一个一次性 Google 账号(VPS 长期登录有风控冻结风险),非个人主号。
        </li>
        <li>
          <strong>失效预警</strong>:5 分钟一次的 `cookies_health_check` cron 自己跑探测,死了会在 Telegram 告警 + 顶部红色 pill。
        </li>
        <li>
          <strong>归档影响</strong>:cookies 清空 / 死亡 → `archive_bb_deliverables` 静默 skip(不报错只 log skip)。
        </li>
      </ul>
    </div>
  );
}
